import Link from "next/link";
import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Heading,
  HStack,
  List,
  ListIcon,
  ListItem,
  SimpleGrid,
  Text,
  VStack,
} from "@chakra-ui/react";
import { ArrowRight, CheckCircle, XCircle } from "lucide-react";
import {
  BARBER_BENEFITS,
  CUSTOMER_BENEFITS,
  FEATURES,
  HOME_SECTION_IDS,
  HOW_IT_WORKS,
  PRIMARY_CTA_HREF,
  PRIMARY_CTA_LABEL,
  PROBLEM_ITEMS,
  TRUST_PROOF,
  TRUST_STRIP,
} from "./homeContent";
import { AGENDA_LANDING_PATH } from "@/components/agenda-landing/agendaLandingContent";
import {
  HomeCard,
  SectionContainer,
  SectionHeading,
  SectionShell,
} from "./homeUi";

export function HomeTrustStrip() {
  return (
    <Box as="section" py={{ base: 8, md: 10 }} bg="barber.900" borderY="1px solid" borderColor="whiteAlpha.100">
      <SectionContainer>
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={{ base: 4, md: 8 }}>
          {TRUST_STRIP.map(({ label, icon: Icon }) => (
            <HStack key={label} spacing={3} justify={{ base: "center", md: "flex-start" }}>
              <Box color="orange.900">
                <Icon size={22} />
              </Box>
              <Text fontSize="sm" fontWeight="medium" color="white">
                {label}
              </Text>
            </HStack>
          ))}
        </SimpleGrid>
      </SectionContainer>
    </Box>
  );
}

export function HomeProblemSolution() {
  return (
    <SectionShell alt>
      <SectionContainer>
        <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={10} alignItems="center">
          <Box>
            <SectionHeading
              align="left"
              eyebrow="Problema e solução"
              title="Sua barbearia não precisa depender de planilhas e anotações soltas"
              description="Quando agenda, clientes e equipe ficam espalhados, o dia fica mais difícil do que deveria."
            />
            <VStack align="start" spacing={3}>
              {PROBLEM_ITEMS.map((item) => (
                <HStack key={item} align="start" spacing={3}>
                  <XCircle size={18} color="#fba931" style={{ flexShrink: 0, marginTop: 2 }} />
                  <Text color="barber.100">{item}</Text>
                </HStack>
              ))}
            </VStack>
          </Box>

          <HomeCard>
            <Heading as="h3" size="lg" color="white" mb={4}>
              Meu Barbeiro PRO centraliza tudo
            </Heading>
            <Text color="barber.100" mb={4}>
              Um sistema para barbearia pensado para organizar agenda, clientes,
              serviços e equipe com clareza — do celular ou do computador.
            </Text>
            <List spacing={3}>
              {[
                "Agenda online com visão clara dos horários",
                "Cadastro e histórico de clientes",
                "Gestão de barbeiros e serviços",
                "Link público para agendamento",
              ].map((item) => (
                <ListItem key={item} color="barber.100">
                  <ListIcon as={CheckCircle} color="orange.900" />
                  {item}
                </ListItem>
              ))}
            </List>
          </HomeCard>
        </Grid>
      </SectionContainer>
    </SectionShell>
  );
}

export function HomeFeatures() {
  return (
    <SectionShell id={HOME_SECTION_IDS.recursos}>
      <SectionContainer>
        <SectionHeading
          eyebrow="Recursos principais"
          title="Tudo o que sua barbearia precisa para operar com mais clareza"
          description="Funcionalidades reais do Meu Barbeiro PRO, com foco no que gera organização no dia a dia."
        />

        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
          {FEATURES.map(({ title, description, icon: Icon }) => (
            <HomeCard key={title} h="full">
              <Box color="orange.900" mb={4}>
                <Icon size={28} />
              </Box>
              <Heading as="h3" size="md" color="white" mb={3}>
                {title}
              </Heading>
              <Text color="barber.100" fontSize="sm" mb={3}>
                {description}
              </Text>
              {title === "Agendamento online" ? (
                <Button
                  as={Link}
                  href={AGENDA_LANDING_PATH}
                  variant="link"
                  color="orange.900"
                  fontSize="sm"
                  rightIcon={<ArrowRight size={14} />}
                  p={0}
                  h="auto"
                  minH="auto"
                >
                  Conheça a agenda online
                </Button>
              ) : null}
            </HomeCard>
          ))}
        </SimpleGrid>
      </SectionContainer>
    </SectionShell>
  );
}

export function HomeHowItWorks() {
  return (
    <SectionShell id={HOME_SECTION_IDS.comoFunciona} alt>
      <SectionContainer>
        <SectionHeading
          eyebrow="Como funciona"
          title="Comece em poucos passos"
          description="Do cadastro à operação: configure sua barbearia e coloque a gestão no eixo."
        />

        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
          {HOW_IT_WORKS.map((step) => (
            <HomeCard key={step.step} textAlign={{ base: "left", md: "center" }}>
              <Flex
                align="center"
                justify={{ base: "flex-start", md: "center" }}
                w={10}
                h={10}
                borderRadius="full"
                bg="orange.900"
                color="black"
                fontWeight="bold"
                mb={4}
              >
                {step.step}
              </Flex>
              <Heading as="h3" size="md" color="white" mb={3}>
                {step.title}
              </Heading>
              <Text color="barber.100" fontSize="sm">
                {step.description}
              </Text>
            </HomeCard>
          ))}
        </SimpleGrid>
      </SectionContainer>
    </SectionShell>
  );
}

export function HomeBarberBenefits() {
  return (
    <SectionShell id={HOME_SECTION_IDS.beneficios}>
      <SectionContainer>
        <SectionHeading
          eyebrow="Para a barbearia"
          title="Mais controle, menos improviso no dia a dia"
          description="Benefícios práticos para quem gerencia agenda, equipe e atendimentos."
        />

        <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={4} maxW="5xl" mx="auto">
          {BARBER_BENEFITS.map((benefit) => (
            <HStack
              key={benefit}
              align="start"
              spacing={3}
              bg="barber.900"
              border="1px solid"
              borderColor="whiteAlpha.100"
              borderRadius="xl"
              p={4}
            >
              <CheckCircle size={18} color="#fba931" style={{ flexShrink: 0, marginTop: 2 }} />
              <Text color="barber.100" fontSize="sm">
                {benefit}
              </Text>
            </HStack>
          ))}
        </SimpleGrid>
      </SectionContainer>
    </SectionShell>
  );
}

export function HomeCustomerBenefits() {
  return (
    <SectionShell id={HOME_SECTION_IDS.clientes} alt>
      <SectionContainer>
        <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={10} alignItems="center">
          <Box>
            <SectionHeading
              align="left"
              eyebrow="Para o cliente final"
              title="Seus clientes também ganham com agendamento online"
              description="Cada barbearia pode disponibilizar uma página pública para consulta de horários e agendamentos."
            />
            <List spacing={3}>
              {CUSTOMER_BENEFITS.map((item) => (
                <ListItem key={item} color="barber.100">
                  <ListIcon as={CheckCircle} color="orange.900" />
                  {item}
                </ListItem>
              ))}
            </List>
          </Box>

          <HomeCard>
            <Text color="orange.900" fontSize="sm" fontWeight="bold" mb={2}>
              Exemplo de link público
            </Text>
            <Text color="white" fontWeight="semibold" mb={2}>
              meubarbeiropro.com/agendar/sua-barbearia
            </Text>
            <Text color="barber.100" fontSize="sm">
              Seus clientes escolhem serviço, data e horário disponível. Você
              gerencia tudo pelo painel da barbearia.
            </Text>
          </HomeCard>
        </Grid>
      </SectionContainer>
    </SectionShell>
  );
}

export function HomeTrustProof() {
  return (
    <SectionShell>
      <SectionContainer>
        <SectionHeading
          eyebrow="Confiança"
          title="Feito para barbearias que querem organização de verdade"
          description="Sem promessas exageradas — apenas o que o produto entrega hoje."
        />

        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
          {TRUST_PROOF.map(({ title, description, icon: Icon }) => (
            <HomeCard key={title} h="full">
              <Box color="orange.900" mb={3}>
                <Icon size={24} />
              </Box>
              <Heading as="h3" size="sm" color="white" mb={2}>
                {title}
              </Heading>
              <Text color="barber.100" fontSize="sm">
                {description}
              </Text>
            </HomeCard>
          ))}
        </SimpleGrid>
      </SectionContainer>
    </SectionShell>
  );
}

export function HomeMidCta() {
  return (
    <Box py={{ base: 12, md: 16 }} bg="barber.900">
      <SectionContainer>
        <Flex
          direction={{ base: "column", md: "row" }}
          align="center"
          justify="space-between"
          gap={6}
          bg="linear-gradient(135deg, rgba(251,169,49,0.12) 0%, rgba(27,28,41,0.9) 100%)"
          border="1px solid"
          borderColor="whiteAlpha.150"
          borderRadius="2xl"
          px={{ base: 6, md: 10 }}
          py={{ base: 8, md: 10 }}
        >
          <Box textAlign={{ base: "center", md: "left" }}>
            <Heading as="h2" size="lg" color="white" mb={2}>
              Comece a organizar sua barbearia hoje
            </Heading>
            <Text color="barber.100">
              Teste grátis por 7 dias e conheça o sistema na prática.
            </Text>
          </Box>
          <Button
            as={Link}
            href={PRIMARY_CTA_HREF}
            size="lg"
            bg="button.cta"
            color="black"
            rightIcon={<ArrowRight size={16} />}
            flexShrink={0}
            _hover={{ opacity: 0.85 }}
          >
            {PRIMARY_CTA_LABEL}
          </Button>
        </Flex>
      </SectionContainer>
    </Box>
  );
}
